import { create } from 'zustand';
import { supabase } from '../lib/supabase';

type OnboardingStep = 1 | 2 | 3 | 4 | 5 | 6;

interface OnboardingState {
  loaded: boolean;
  stepsCompleted: Record<OnboardingStep, boolean>;
  dismissedSteps: Set<OnboardingStep>;
  /**
   * Separate from the numbered onboarding cascade. Tracks whether the
   * invited-protagonist 5-step welcome cascade has been seen. Set to
   * true on completion or skip; once true, the cascade never fires
   * again for this user.
   */
  invitedIntroSeen: boolean;
  loadProgress: (userId: string) => Promise<void>;
  completeStep: (step: OnboardingStep) => Promise<void>;
  dismissStep: (step: OnboardingStep) => void;
  dismissCurrentStep: () => void;
  resetDismissals: () => void;
  skipAllSteps: () => Promise<void>;
  shouldShowStep: (step: OnboardingStep) => boolean;
  markInvitedIntroSeen: () => Promise<void>;
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  loaded: false,
  stepsCompleted: { 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
  dismissedSteps: new Set(),
  invitedIntroSeen: false,

  loadProgress: async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .select('step_1_completed, step_2_completed, step_3_completed, step_4_completed, step_5_completed, step_6_completed, invited_intro_seen')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        set({ loaded: true });
        return;
      }

      if (data) {
        set({
          loaded: true,
          stepsCompleted: {
            1: data.step_1_completed,
            2: data.step_2_completed,
            3: data.step_3_completed,
            4: data.step_4_completed,
            5: data.step_5_completed ?? false,
            6: data.step_6_completed ?? false,
          },
          invitedIntroSeen: data.invited_intro_seen ?? false,
        });
      } else {
        const { error: insertError } = await supabase
          .from('user_onboarding_progress')
          .insert({ user_id: userId });

        if (insertError) {
          console.warn('[Onboarding] Could not create progress row:', insertError.message);
        }

        set({ loaded: true });
      }
    } catch {
      set({ loaded: true });
    }
  },

  completeStep: async (step: OnboardingStep) => {
    const { stepsCompleted } = get();
    if (stepsCompleted[step]) return;

    const columnName = `step_${step}_completed`;

    set({
      stepsCompleted: { ...stepsCompleted, [step]: true },
    });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // UPSERT instead of UPDATE — fixes a race where a brand-new user's
      // INSERT (from loadProgress) hadn't committed yet by the time the
      // user completes their first tutorial step. Plain UPDATE silently
      // affects 0 rows and the step is never persisted, so the tutorial
      // restarts on the next session.
      await supabase
        .from('user_onboarding_progress')
        .upsert(
          {
            user_id: user.id,
            [columnName]: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' },
        );
    } catch {
      // Non-critical — local state is already updated
    }
  },

  dismissStep: (step: OnboardingStep) => {
    const { dismissedSteps } = get();
    const newDismissed = new Set(dismissedSteps);
    newDismissed.add(step);
    set({ dismissedSteps: newDismissed });
  },

  dismissCurrentStep: () => {
    // Find the current active step and dismiss it
    const { stepsCompleted, dismissedSteps } = get();
    for (let i = 1; i <= 6; i++) {
      const step = i as OnboardingStep;
      if (!stepsCompleted[step] && !dismissedSteps.has(step)) {
        const newDismissed = new Set(dismissedSteps);
        newDismissed.add(step);
        set({ dismissedSteps: newDismissed });
        break;
      }
    }
  },

  resetDismissals: () => {
    set({ dismissedSteps: new Set() });
  },

  skipAllSteps: async () => {
    const allStepsCompleted: Record<OnboardingStep, boolean> = {
      1: true,
      2: true,
      3: true,
      4: true,
      5: true,
      6: true,
    };

    set({
      stepsCompleted: allStepsCompleted,
    });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('user_onboarding_progress')
        .update({
          step_1_completed: true,
          step_2_completed: true,
          step_3_completed: true,
          step_4_completed: true,
          step_5_completed: true,
          step_6_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);
    } catch {
      // Non-critical — local state is already updated
    }
  },

  shouldShowStep: (step: OnboardingStep) => {
    const { loaded, stepsCompleted, dismissedSteps } = get();
    if (!loaded) return false;
    if (stepsCompleted[step]) return false;
    if (dismissedSteps.has(step)) return false;
    for (let i = 1; i < step; i++) {
      if (!stepsCompleted[i as OnboardingStep]) return false;
    }
    return true;
  },

  markInvitedIntroSeen: async () => {
    if (get().invitedIntroSeen) return;
    set({ invitedIntroSeen: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase
        .from('user_onboarding_progress')
        .upsert(
          {
            user_id: user.id,
            invited_intro_seen: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' },
        );
    } catch {
      // Non-critical — local state is already updated.
    }
  },
}));
